import { useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useCreateTourPlan } from '../hooks/useCreateTourPlan'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import { useSelector } from 'react-redux'

import { Box, Divider, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  Button,
  DrawerScrollContainer,
  FormInputControl,
} from '@/components/ui'
import { SelectCheckpointFlatListItem } from '@/features/guard-checkpoint-flat-list'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { updateCheckPointIds } from '../utils/utils'
import { CreateTourPlanHeader } from './CreateTourPlanHeader'

interface CreateTourPlanIProps {
  facilityId: string
  currentFloorId: string
  onClose: () => void
}

export const CreateTourPlan = ({
  facilityId,
  currentFloorId,
  onClose,
}: CreateTourPlanIProps) => {
  const me = useSelector(selectMe)
  const { handleSubmit, register, errors, formState } = useForm({
    mode: 'onChange',
  })
  const { isValid } = formState
  const { showSuccess, showError } = useToasts()
  const { floorData, isCreateLoading, create } = useCreateTourPlan(facilityId)
  const [checkpointIds, setCheckpointIds] = useState<string[]>([])
  const { setHighlightedDeviceIds } = useFloorPlan()

  const handleChecked = (id: string) => {
    const ids = updateCheckPointIds(checkpointIds, id)
    setHighlightedDeviceIds(ids)
    setCheckpointIds(ids)
  }

  const onSubmit = async (values) => {
    const input = {
      name: values.name,
      facilityId,
      checkpointIds,
    }
    try {
      await create(input)
      showSuccess('TourPlan created.')
      onClose()
      mixpanel.track(
        'Button Press: Facility Tour Plans - Create Tour Plan - Create',
        {
          facility_id: facilityId,
          role: me.roleName,
        }
      )
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleOnClose = () => {
    onClose()
    setHighlightedDeviceIds(null)
    mixpanel.track(
      'Button Press: Facility Tour Plans - Create Tour Plan - Cancel',
      {
        facility_id: facilityId,
        role: me.roleName,
      }
    )
  }

  return (
    <>
      <CreateTourPlanHeader onClose={handleOnClose} />

      <Box mb={3} padding='16px'>
        <FormInputControl
          data-testid='tourplan_createTourPlan_name'
          errorMessage='You must specify a name'
          id='name'
          inputRef={register({
            required: 'You must specify an name',
            pattern: /\S/,
          })}
          isInvalid={errors.name}
          label='Tour Plan Name'
          placeholder='Enter Tour Plan Name'
          type='name'
        />
      </Box>
      <DrawerScrollContainer padding='16px'>
        <AnimatePresence>
          {floorData?.floors?.edges?.length > 0 ? (
            <Box
              animate={{ opacity: 1 }}
              as={motion.div}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              w='full'
            >
              {floorData?.floors?.edges?.map((f) => (
                <VStack align='stretch' key={f.node?.id}>
                  <Box
                    color='#172B4D'
                    fontSize='18px'
                    fontWeight='medium'
                    paddingY='10px'
                  >
                    {f?.node?.id === currentFloorId
                      ? 'Current Floor'
                      : f?.node?.name}
                  </Box>
                  {f?.node?.devices?.edges.map((d) => (
                    <SelectCheckpointFlatListItem
                      key={d.node?.id}
                      label={d.node?.name}
                      onChecked={() => handleChecked(d.node?.id)}
                      subLabel={d.node?.checkpoint?.displayId}
                    />
                  ))}
                </VStack>
              ))}
            </Box>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              List is empty
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
      <Divider
        bgColor='#D7D4D4'
        display='flex'
        h='2px'
        justifySelf='flex-end'
        mb='10px'
      />

      <Box
        display='flex'
        flexDir='row'
        justifyContent='space-between'
        mb='40px'
        padding='16px'
      >
        <Button mr='3' onClick={handleOnClose} variant='ghost'>
          Cancel
        </Button>
        <Button
          isDisabled={!isValid || !checkpointIds.length}
          isLoading={isCreateLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Saving'
          onClick={handleSubmit(onSubmit)}
        >
          Create
        </Button>
      </Box>
    </>
  )
}
